<template>
  <div
    class="stepper-container"
    :class="{
      'left-arrow-hidden': paginationEnabled && pagination.currentPage <= 1,
      'right-arrow-hidden':
        paginationEnabled && pagination.currentPage >= totalPages,
    }"
  >
    <div
      class="pagination left"
      role="pagination"
      v-if="paginationEnabled"
      @click="goBack"
    >
      <ChevronLeftIcon
        class="pagination-icon"
        v-if="paginationEnabled && pagination.currentPage > 1"
      />
    </div>
    <div class="stepper">
      <div class="stepper-inner">
        <div
          v-for="(item, index) in paginatedItems"
          :key="item.name"
          :class="['step', `step-${item.status}`]"
          :step-count="paginatedItems.length"
        >
          <div class="step-icons">
            <CheckCircleIcon
              class="step-icon"
              v-if="item.status === 'complete'"
            />
            <QuestionMarkCircleIcon
              class="step-icon"
              v-else-if="item.status === 'active'"
            />
            <ExclamationCircleIcon class="step-icon" v-else />
          </div>
          <span class="step-title" v-html="item.name"></span>
          <div class="step-content">
            <div class="stat">{{ item.date }}</div>
          </div>
          <div
            v-if="showArrow(index)"
            class="step-arrow"
            :show-arrow="showArrow(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="arrow-icon"
              :class="['arrow-na', { 'arrow-na': item.date === 'N/A' }]"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill="currentColor"
                d="M11 16H3V8h8V2l10 10l-10 10zm2-9v3H5v4h8v3l5-5z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
    <div
      class="pagination right"
      role="pagination"
      v-if="paginationEnabled && pagination.currentPage < totalPages"
      @click="goNext"
    >
      <ChevronRightIcon class="pagination-icon" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from "vue";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { ProductIds } from "@/types/types";
import { ApplicationStatusStep } from "@/types/stepper";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/solid";

const applicationStore = useApplicationStore();

const stats = ref<Array<ApplicationStatusStep>>([]);

const productTypeId = computed(() => applicationStore.viewModel.productTypeId);

const steps = computed(
  () => applicationStore.viewModel.applicationStatusStepper,
);

const paginationEnabled = computed<boolean>(() => {
  switch (productTypeId.value) {
    case ProductIds.PEL:
      return true;
    case ProductIds.LOC:
      return true;
    default:
      return false;
  }
});

const pagination = reactive({
  currentPage: 1,
  perPage: computed<number>((): number => {
    switch (productTypeId.value) {
      case ProductIds.PEL:
      case ProductIds.LOC:
        if (window.innerWidth < 375) {
          return 1;
        }
        return 3;
      case ProductIds.REFI:
        return steps.value.length;
      default:
        return steps.value.length;
    }
  }).value,
  totalItems: computed<number>((): number => stats.value.length),
  totalPages: computed<number>(() =>
    Math.ceil((steps.value || []).length / pagination.perPage),
  ),
  pages: computed<Array<number>>(() =>
    [...Array(pagination.totalPages).keys()].map((i) => i + 1),
  ),
});

const totalPages = computed<number>(() =>
  Math.ceil((stats.value || []).length / pagination.perPage),
);

const paginatedItems = computed(() => {
  const { currentPage, perPage, totalItems } = pagination;

  if (!currentPage) {
    return stats.value;
  } else {
    const start = (currentPage - 1) * perPage;
    let stop = start + perPage;
    let itemCount = Math.abs(stop - start);
    let numberOfItemsToPush = 0;

    if (stop > totalItems) {
      numberOfItemsToPush = Math.abs(stop - totalItems);
      stop -= numberOfItemsToPush;
      itemCount = Math.abs(stop - start);
    }

    const newItems = stats.value.slice(start, stop);

    return newItems;
  }
});

async function goNext() {
  if (pagination.currentPage >= pagination.totalPages) {
    pagination.currentPage = 1;
  } else {
    pagination.currentPage++;
  }
}

async function goBack() {
  if (pagination.currentPage === 1) {
    pagination.currentPage = pagination.pages[pagination.pages.length - 1];
  } else {
    pagination.currentPage--;
  }
}

function showArrow(index: number): boolean {
  if (
    productTypeId.value === ProductIds.PEL ||
    productTypeId.value === ProductIds.LOC
  ) {
    return false;
  } else {
    return index < stats.value.length - 1;
  }
}

onMounted(() => {
  stats.value = steps.value;
});
</script>

<style scoped>
.stepper-container.left-arrow-hidden,
.stepper-container.left-arrow-hidden .stepper,
.stepper-container.left-arrow-hidden .stepper .stepper-inner {
  border-radius: 12px 0px 0px 12px;
}

.stepper-container.right-arrow-hidden,
.stepper-container.right-arrow-hidden .stepper,
.stepper-container.right-arrow-hidden .stepper .stepper-inner {
  border-radius: 0px 12px 12px 0px;
}

.stepper-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-end;
}
.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  max-width: 1431px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  position: relative;
  background: #f2f3f5;
}

.stepper-inner {
  background: #fff;
  display: flex;
  width: 100%;
}
.stepper .step:is([step-count="1"]) {
  max-width: 150px;
}

.stepper .step:is([step-count="1"]) .step-icons {
  text-align: center;
}
.stepper .step:is([step-count="1"]) .step-content {
  text-align: center;
}

.step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.2rem;
  transition: color 0.3s ease;
  position: relative;
  border-radius: 12px 0px 0px 12px;
}

.step-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -10px;
  z-index: 1;
  height: 100%;
}

.step-arrow .arrow-icon {
  width: 20px;
  height: 20px;
  margin-top: 0.1rem;
}

.step-icons {
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon {
  width: 28px;
  height: 28px;
}

.step-active {
  color: #f7941d;
}

.step-complete {
  color: #48a842;
}

.step-alert {
  color: #e31838;
}

.step-na {
  color: #e0e0e0;
}

.arrow-na {
  fill: #e0e0e0;
}

.step-title {
  font-family: var(--sc-montserrat-font-family);
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  margin-top: 5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  max-width: 100%;
  text-align: center;
  font-style: normal;
  line-height: normal;
}

.step-content {
  font-size: 0.75rem;
}

.stat {
  margin-top: 5px;
  margin-bottom: 5px;
  text-align: center;
  font-family: var(--sc-montserrat-font-family);
  font-size: 16px;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: normal;
}

div[role="pagination"] {
  display: flex;
  align-items: center;
  background: #f2f3f5;
  cursor: pointer;
}

.pagination-icon {
  color: #000;
  height: 28px;
  height: 28px;
  font-weight: 700;
}

div[role="pagination"].left {
  border-radius: 12px 0px 0px 12px;
}

div[role="pagination"].right {
  border-radius: 0px 12px 12px 0px;
}

@media screen and (max-width: 768px) {
  .stepper-container {
    justify-content: flex-start;
    margin-bottom: 25px;
  }
}
</style>
